import React, { useRef, useEffect, useState } from "react";
import { Send, Square, Paperclip, Sparkles, Gauge, Layers, Heart, Brain, X, FileText, Mic, MicOff, Volume2, VolumeX, ShieldAlert } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "./ui/tooltip";
import { toast } from "sonner";
import api, { API_BASE, errText } from "../lib/api";
import useVoice from "../hooks/useVoice";

const MODES = [
  { id: "life_assistant", label: "Life Assistant", icon: Heart, desc: "Practical, action-first. Handles tasks and memories inline." },
  { id: "fast", label: "Fast", icon: Gauge, desc: "Direct, single-model answer" },
  { id: "standard", label: "Standard", icon: Sparkles, desc: "Structured, quality-first" },
  { id: "orchestrated", label: "Orchestrated Review", icon: Layers, desc: "Specialist + evaluator (up to 3 calls)" },
  { id: "agentic_brain", label: "Agentic Brain", icon: Brain, desc: "Stella Brain — planner, specialists, critic, and approval gate." },
];

const CONTEXTS = [
  { id: "general", label: "General" },
  { id: "work", label: "Work" },
  { id: "private", label: "Private" },
  { id: "project", label: "Project" },
];

const ACCEPT = ".pdf,.docx,.txt,.csv,.xlsx,.pptx,.png,.jpg,.jpeg";

export default function Composer({
  value, onChange, onSend, onStop, streaming,
  mode, setMode, context, setContext,
  provider, model, registry, onPickModel,
  projects, projectId, setProjectId,
  attachments = [], onAttachmentsChange,
  voiceStatus,        // { stt: {available,reason}, tts: {available,reason}, confidential_mode }
  ttsEnabled, setTtsEnabled,
}) {
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Voice: prefer managed STT; fallback to browser Web Speech API.
  const managedSttOn = !!voiceStatus?.stt?.available;
  const confidential = !!voiceStatus?.confidential_mode;
  const [managedRecording, setManagedRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);

  const browserVoice = useVoice({
    onFinal: (finalText) => {
      // Append final transcript to composer input.
      onChange(((value || "") + (value ? " " : "") + finalText).trim());
    },
  });

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(220, ta.scrollHeight) + "px";
  }, [value]);

  const submit = (e) => {
    e?.preventDefault?.();
    const text = (value || "").trim();
    if (!text || streaming) return;
    onSend(text);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const currentModelLabel = React.useMemo(() => {
    if (!registry?.providers) return `${provider} · ${model}`;
    const p = registry.providers[provider];
    const m = p?.models?.find((mm) => mm.id === model);
    return m ? `${p.name} · ${m.label}` : `${provider} · ${model}`;
  }, [provider, model, registry]);

  const pickFiles = () => fileRef.current?.click();

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          const { data } = await api.post("/files", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploaded.push({
            id: data.id, filename: data.filename,
            mime_type: data.mime_type, size_bytes: data.size_bytes,
            text_extracted: data.text_extracted,
          });
        } catch (err) {
          toast.error(errText(err, `Could not attach ${f.name}`));
        }
      }
      if (uploaded.length) onAttachmentsChange?.([...(attachments || []), ...uploaded]);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAtt = (id) => {
    onAttachmentsChange?.((attachments || []).filter((a) => a.id !== id));
  };

  // ---- Managed STT (tap-to-talk) ----
  const stopManagedRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch {}
    }
    mediaRecorderRef.current = null;
    audioStreamRef.current?.getTracks?.().forEach((t) => t.stop());
    audioStreamRef.current = null;
    setManagedRecording(false);
  };

  const startManagedRecording = async () => {
    if (confidential) {
      toast.message("Voice is disabled", { description: "Confidential mode is on. Voice input and playback are turned off." });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      const mimeType = types.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = rec;
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) audioChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        setTranscribing(true);
        try {
          const blob = new Blob(audioChunksRef.current, { type: rec.mimeType || "audio/webm" });
          const fd = new FormData();
          const ext = (rec.mimeType || "audio/webm").includes("mp4") ? "mp4"
                    : (rec.mimeType || "").includes("wav") ? "wav" : "webm";
          fd.append("audio", blob, `recording.${ext}`);
          const { data } = await api.post("/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (data?.text || "").trim();
          if (text) {
            onChange(((value || "") + (value ? " " : "") + text).trim());
          } else {
            toast.message("No speech detected");
          }
        } catch (err) {
          const code = err?.response?.data?.detail;
          if (code === "voice_setup_required") toast.message("Voice not configured", { description: "Managed STT unavailable — falling back to browser voice where supported." });
          else if (code === "confidential_mode_on") toast.message("Voice is disabled", { description: "Confidential mode is on." });
          else toast.error(errText(err, "Transcription failed"));
        } finally {
          setTranscribing(false);
          audioChunksRef.current = [];
        }
      };
      rec.start();
      setManagedRecording(true);
    } catch (err) {
      toast.error("Microphone permission denied");
      setManagedRecording(false);
    }
  };

  const toggleMic = () => {
    if (confidential) {
      toast.message("Voice is disabled", { description: "Confidential mode is on." });
      return;
    }
    if (managedSttOn) {
      if (managedRecording) stopManagedRecording();
      else startManagedRecording();
      return;
    }
    // Managed unavailable → try browser SpeechRecognition
    if (!browserVoice.supported) {
      toast.message("Voice mode coming soon", { description: "Your browser or account doesn't have voice yet." });
      return;
    }
    if (browserVoice.listening) browserVoice.stop();
    else browserVoice.start();
  };

  const micActive = managedRecording || browserVoice.listening;
  const micDisabled = confidential || (!managedSttOn && !browserVoice.supported) || transcribing;
  const micLabel = confidential ? "Voice disabled (confidential mode)"
                   : transcribing ? "Transcribing…"
                   : managedSttOn ? (managedRecording ? "Stop recording" : "Tap to talk — audio never stored")
                   : browserVoice.supported ? (browserVoice.listening ? "Stop listening" : "Tap to talk (browser)")
                   : "Voice mode coming soon";

  return (
    <div className="w-full max-w-3xl mx-auto px-3 sm:px-5 pb-4 sm:pb-6">
      {/* Controls row (above composer) */}
      <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="composer-controls">
        {/* Model picker */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="h-8 px-3 rounded-full bg-white border border-[#E5E4DE] text-xs font-medium text-slate-700 hover:border-indigo-300 hover:text-slate-900 inline-flex items-center gap-1.5"
              data-testid="composer-model-picker"
            >
              <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
              {currentModelLabel}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Quick picks</DropdownMenuLabel>
            {registry?.aliases?.map((a) => (
              <DropdownMenuItem
                key={a.id}
                onSelect={(e) => {
                  if (a.status === "setup_required") {
                    e?.preventDefault?.();
                    toast.message("Setup required", {
                      description: `${a.label} needs its provider key. Ask your administrator to configure it.`,
                    });
                    return;
                  }
                  onPickModel(a.provider, a.model);
                }}
                data-testid={`alias-${a.id}`}
                aria-disabled={a.status === "setup_required"}
                className={a.status === "setup_required" ? "opacity-60" : ""}
              >
                <div className="flex flex-col">
                  <span className="font-medium text-sm">{a.label}</span>
                  <span className="text-[11px] text-slate-500">
                    {a.status === "setup_required" ? "Setup required" : `${a.provider} · ${a.model}`}
                  </span>
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">By provider</DropdownMenuLabel>
            {registry?.providers && Object.entries(registry.providers).map(([pid, p]) => (
              <div key={pid} className="px-2 py-1">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-800 py-1">
                  {p.name}
                  {p.status === "setup_required" ? (
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Setup required</span>
                  ) : p.key_source === "PREVIEW_ONLY" ? (
                    <span className="text-[10px] uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">Preview</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Ready</span>
                  )}
                </div>
                {p.models.map((m) => (
                  <button
                    key={m.id}
                    disabled={p.status === "setup_required"}
                    onClick={() => {
                      if (p.status === "setup_required") return;
                      onPickModel(pid, m.id);
                    }}
                    className={`w-full text-left px-2 py-1 rounded text-[13px] ${
                      provider === pid && model === m.id
                        ? "bg-indigo-50 text-indigo-900"
                        : "hover:bg-slate-100 text-slate-700"
                    } disabled:opacity-50`}
                    data-testid={`model-${pid}-${m.id}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Mode pills */}
        <div className="flex items-center gap-1 bg-white border border-[#E5E4DE] rounded-full p-0.5" data-testid="mode-pills">
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <TooltipProvider key={m.id} delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setMode(m.id)}
                      data-testid={`mode-${m.id}`}
                      className={`h-7 px-2.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 ${
                        active ? "bg-indigo-600 text-white" : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{m.label}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{m.desc}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>

        {/* Context switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="h-8 px-3 rounded-full bg-white border border-[#E5E4DE] text-xs font-medium text-slate-700 hover:border-indigo-300 hover:text-slate-900"
              data-testid="composer-context-picker"
            >
              {CONTEXTS.find((c) => c.id === context)?.label || "General"}
              {context === "project" && projectId && (
                <span className="ml-1 text-indigo-700">· {projects.find((p) => p.id === projectId)?.name}</span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {CONTEXTS.filter((c) => c.id !== "project").map((c) => (
              <DropdownMenuItem key={c.id} onSelect={() => setContext(c.id)} data-testid={`context-${c.id}`}>
                {c.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
              Project context
            </DropdownMenuLabel>
            {(projects || []).length === 0 && (
              <div className="px-2 py-1.5 text-xs text-slate-500">No projects yet.</div>
            )}
            {(projects || []).map((p) => (
              <DropdownMenuItem
                key={p.id}
                onSelect={() => { setContext("project"); setProjectId(p.id); }}
                data-testid={`context-project-${p.id}`}
              >
                {p.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Attachment chips */}
      {attachments?.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5" data-testid="composer-attachments">
          {attachments.map((a) => (
            <div key={a.id}
                 className="inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 text-indigo-800 rounded-full pl-2 pr-1 py-0.5 text-[11px]"
                 data-testid={`attachment-chip-${a.id}`}>
              <FileText className="w-3 h-3" />
              <span className="truncate max-w-[220px]">{a.filename}</span>
              <button
                type="button"
                onClick={() => removeAtt(a.id)}
                className="w-4 h-4 rounded-full hover:bg-indigo-100 inline-flex items-center justify-center"
                aria-label={`Remove ${a.filename}`}
                data-testid={`attachment-remove-${a.id}`}
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Composer container (LIGHT — no black bar) */}
      <form
        onSubmit={submit}
        className="bg-white border border-slate-200/80 shadow-[0_4px_20px_rgba(67,56,202,0.06)] focus-within:border-indigo-400 focus-within:ring-4 focus-within:ring-indigo-500/10 rounded-2xl transition-colors duration-200 p-2 sm:p-3"
        data-testid="composer-form"
      >
        <textarea
          ref={taRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask Stella anything..."
          rows={1}
          data-testid="composer-textarea"
          className="w-full bg-transparent border-0 resize-none focus:outline-none focus:ring-0 text-slate-900 placeholder:text-slate-400 text-base leading-relaxed min-h-[44px] max-h-[220px] px-2 py-1.5"
          style={{ fontSize: "16px" }}
        />
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-1">
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="composer-attach-input"
            />
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={pickFiles}
                    data-testid="composer-attach"
                    className="p-2 rounded-lg text-slate-500 hover:text-indigo-700 hover:bg-slate-100 disabled:opacity-60"
                    aria-label="Attach files"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {uploading ? "Uploading…" : "Attach PDF, DOCX, TXT, CSV, XLSX, PPTX, PNG, JPG (≤15MB)"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Mic — tap to talk */}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    disabled={micDisabled}
                    onClick={toggleMic}
                    data-testid="composer-mic"
                    aria-label={micLabel}
                    className={`p-2 rounded-lg disabled:opacity-40 ${
                      micActive
                        ? "text-red-600 bg-red-50 animate-pulse"
                        : "text-slate-500 hover:text-indigo-700 hover:bg-slate-100"
                    }`}
                  >
                    {confidential ? <ShieldAlert className="w-4 h-4" />
                     : micActive ? <MicOff className="w-4 h-4" />
                     : <Mic className="w-4 h-4" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">{micLabel}</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* TTS toggle — read replies aloud */}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    disabled={confidential || !voiceStatus?.tts?.available && !("speechSynthesis" in window)}
                    onClick={() => setTtsEnabled?.(!ttsEnabled)}
                    data-testid="composer-tts-toggle"
                    aria-pressed={!!ttsEnabled}
                    aria-label={ttsEnabled ? "Turn read-aloud off" : "Turn read-aloud on"}
                    className={`p-2 rounded-lg disabled:opacity-40 ${
                      ttsEnabled
                        ? "text-indigo-700 bg-indigo-50"
                        : "text-slate-500 hover:text-indigo-700 hover:bg-slate-100"
                    }`}
                  >
                    {ttsEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {confidential
                    ? "Read-aloud disabled (confidential mode)"
                    : voiceStatus?.tts?.available
                      ? (ttsEnabled ? "Stella will read replies aloud (managed voice)" : "Read replies aloud (managed voice)")
                      : ("speechSynthesis" in window
                          ? (ttsEnabled ? "Stella will read replies aloud (browser voice)" : "Read replies aloud (browser voice)")
                          : "Read-aloud not available yet")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {streaming ? (
            <button
              type="button"
              onClick={onStop}
              data-testid="composer-stop"
              className="bg-slate-900 hover:bg-slate-800 text-white p-2.5 rounded-xl shadow-md inline-flex items-center gap-1.5"
              aria-label="Stop generating"
            >
              <Square className="w-4 h-4" />
              <span className="text-xs font-medium hidden sm:inline">Stop</span>
            </button>
          ) : (
            <button
              type="submit"
              disabled={!value.trim() || uploading}
              data-testid="composer-send"
              className="bg-indigo-700 hover:bg-indigo-800 disabled:bg-indigo-300 text-white p-2.5 rounded-xl shadow-md active:scale-95 transition-transform duration-100 inline-flex items-center gap-1.5"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
              <span className="text-xs font-medium hidden sm:inline">Send</span>
            </button>
          )}
        </div>
      </form>
      <div className="mt-2 text-[11px] text-slate-400 px-1">
        Stella can draft external actions but nothing is sent, published, or scheduled without your approval.
        {managedRecording && <span className="ml-2 text-red-600 font-medium">● Recording — tap the mic again to stop. Audio never leaves your session.</span>}
        {browserVoice.listening && !managedSttOn && <span className="ml-2 text-red-600 font-medium">● Listening (browser voice)</span>}
      </div>
    </div>
  );
}
